import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  customFieldOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportUserDistributionByCustomFieldByDateData,
} from '@src/types';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportUserDistributionByCustomFieldByDate,
  getUserDistributionByCustomFieldByDate,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const UserDistributionByCustomFieldByDate: FC = () => {
  // Data state
  const [data, setData] = useState<TReportUserDistributionByCustomFieldByDateData[]>([]);

  // Field selection state
  const [selectedField, setSelectedField] = useState<string>(
    customFieldOptions[0]?.value || ''
  );

  // Determine available field values from data (e.g., English, Spanish, French)
  const [selectedFieldValues, setSelectedFieldValues] = useState<string[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_MONTH,
    defaultGroupBy: ReportGroupBy.WEEK,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, tableData, displayItems } =
    useReportTableColumns<TReportUserDistributionByCustomFieldByDateData>({
      data,
      selectedItems: selectedFieldValues,
      itemsField: 'field_values',
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.field_values[key] || 0,
      getPreviousPeriodField: (item, key) =>
        item.field_values_previous?.[key] || undefined,
      getPreviousYearField: (item, key) =>
        item.field_values_previous_year?.[key] || undefined,
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => value.toLocaleString(),
      includeTotalsRow: true,
    });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.field_values[key] || 0,
    getPreviousPeriodField: (item, key) =>
      item.field_values_previous?.[key] || undefined,
    getPreviousYearField: (item, key) =>
      item.field_values_previous_year?.[key] || undefined,
  });

  // Get available field values from data if none selected
  const fieldValueOptions =
    data.length > 0
      ? Array.from(
          new Set(
            data.flatMap((item) => Object.keys(item.field_values || {}))
          )
        ).map((key) => ({
          label: key,
          value: key,
        }))
      : [];

  const buildPayload = () => ({
    start_date: filters.startDate
      ? dayjs(filters.startDate).format('YYYY-MM-DD')
      : null,
    end_date: filters.endDate ? dayjs(filters.endDate).format('YYYY-MM-DD') : null,
    grouping: (filters.selectedGroupBy || ReportGroupBy.DAY) as ReportGroupBy,
    compare_to_previous_period: filters.compareToPreviousPeriod,
    compare_to_same_period_previous_year:
      filters.compareToSamePeriodPreviousYear,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getUserDistributionByCustomFieldByDate(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const payload = buildPayload();
      const response = await exportUserDistributionByCustomFieldByDate(
        payload,
        'csv'
      );

      downloadFile(response.data, 'user-distribution-by-custom-field-by-date');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        User Distribution By Custom Field By Date
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          categoryOptions={fieldValueOptions}
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          fieldOptions={customFieldOptions}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedCategories={selectedFieldValues}
          selectedField={selectedField}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCategoriesFilter={true}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showFieldFilter={true}
          showGroupByFilter={true}
          onCategoriesChange={setSelectedFieldValues}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onFieldChange={setSelectedField}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name={item}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${item} (Previous Period)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${item} (Previous Year)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              disabled={filters.isLoading || data.length === 0}
              variant="outline"
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
